import { HttpClient, HttpContext, httpResource, HttpResourceRef } from "@angular/common/http";
import { inject, Injectable, ResourceRef, Signal } from "@angular/core";
import { rxResource, toObservable, toSignal } from "@angular/core/rxjs-interop";
import { CACHE_CONFIG, QueryOptions } from "@kno2/core/http";
import { filterNonEmptyProperties, hasAnyValues } from "@kno2/utils";
import { firstValueFrom, Observable, of, switchMap } from "rxjs";
import { DocumentSource } from "../document-sources";
import { OrganizationType } from "../organization-types";
import { Organization, OrganizationSummary } from "./organization.model";

export interface OrganizationServiceAccount {
    id: string;
    clientId: string;
    clientSecret?: string;
    clientIpAddresses?: string[];
    allowedAddressCount: number;
    serviceAccountEnabled: boolean;
    lastLoginDate?: string;
    lastLoginIp?: string;
}

export interface OrganizationsQueryResponse {
    items: OrganizationSummary[];
    totalCount: number;
}

export interface OrganizationsQueryOptions extends QueryOptions {
    cancelling?: boolean;
}

@Injectable({
    providedIn: "root"
})
export class OrganizationsService {
    private readonly httpClient = inject(HttpClient);

    public getOrganizationsResource(query: Signal<OrganizationsQueryOptions>): HttpResourceRef<OrganizationsQueryResponse> {
        return httpResource<OrganizationsQueryResponse>(
            () => {
                const params = filterNonEmptyProperties(query());
                if (!hasAnyValues(params)) {
                    return undefined;
                }

                return {
                    url: "/api/organizations",
                    params
                };
            },
            { defaultValue: { items: [], totalCount: 0 } }
        );
    }

    public getOrganizations(query: Signal<OrganizationsQueryOptions>): Signal<OrganizationsQueryResponse> {
        return toSignal(
            toObservable(query).pipe(
                switchMap((params) => {
                    const filteredParams = filterNonEmptyProperties(params);
                    if (!hasAnyValues(filteredParams)) return of({ items: [], totalCount: 0 });
                    return this.httpClient.get<OrganizationsQueryResponse>(`/api/organizations`, {
                        params: filteredParams
                    });
                })
            ),
            { initialValue: { items: [], totalCount: 0 } }
        );
    }

    public getOrganization(organizationId: string): Signal<Organization> {
        return toSignal(this.getOrganization$(organizationId));
    }

    public getOrganizationAsync(organizationId: string, options?: { reload?: boolean }): Promise<Organization> {
        return firstValueFrom(this.getOrganization$(organizationId, options?.reload));
    }

    private getOrganization$(organizationId: string, reload: boolean = false): Observable<Organization> {
        return this.httpClient.get<Organization>(`/api/organizations/${organizationId}`, {
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true, reload })
        });
    }

    public getOrganizationDocumentSources(organizationId: string): Signal<DocumentSource[]> {
        return toSignal(this.httpClient.get<DocumentSource[]>(`/api/organizations/${organizationId}/documentsources`), { initialValue: [] });
    }

    public getServiceAccounts(organizationId: Signal<Organization["id"]>): ResourceRef<OrganizationServiceAccount[]> {
        return rxResource({
            params: organizationId,
            stream: ({ params: orgId }) => this.httpClient.get<OrganizationServiceAccount[]>(`/api/organizations/${orgId}/serviceaccounts`)
        });
    }

    public getServiceAccount(organizationId: Signal<Organization["id"]>): HttpResourceRef<OrganizationServiceAccount[]> {
        return httpResource(() => `/api/organizations/${organizationId()}/serviceaccount`);
    }

    public getServiceAccountLimit(organizationId: Signal<Organization["id"]>): Signal<number> {
        return toSignal(
            toObservable(organizationId).pipe(switchMap((orgId) => this.httpClient.get<number>(`/api/organizations/${orgId}/serviceaccount/limit`)))
        );
    }

    public updateServiceAccountLimit(organizationId: string, count: number): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/organizations/${organizationId}/serviceaccount/limit`, { count }));
    }

    public updateServiceAccountAddressCount(organizationId: string, serviceAccountId: string, count: number): Promise<void> {
        return firstValueFrom(
            this.httpClient.put<void>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}/ipaddresses/limit`, { count })
        );
    }

    public getTypes(): HttpResourceRef<OrganizationType[]> {
        return httpResource<OrganizationType[]>(() => ({
            url: "/api/organizations/types",
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        }));
    }

    public getSpecialties(): HttpResourceRef<{ id: string; name: string }[]> {
        return httpResource<{ id: string; name: string }[]>(() => ({
            url: "/api/organizations/specialties",
            context: new HttpContext().set(CACHE_CONFIG, { isEnabled: true })
        }));
    }

    public addServiceAccount(organizationId: Organization["id"]): Promise<OrganizationServiceAccount> {
        return firstValueFrom(this.httpClient.put<OrganizationServiceAccount>(`/api/organizations/${organizationId}/serviceaccounts`, null));
    }

    public updateServiceAccount(organizationId: Organization["id"], serviceAccountId: OrganizationServiceAccount["id"]): Promise<OrganizationServiceAccount> {
        return firstValueFrom(
            this.httpClient.put<OrganizationServiceAccount>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}`, null)
        );
    }

    public updateServiceAccountIpAddresses(
        organizationId: Organization["id"],
        serviceAccountId: OrganizationServiceAccount["id"],
        ipAddresses: string[]
    ): Promise<void> {
        return firstValueFrom(this.httpClient.put<void>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}/ipaddresses`, ipAddresses));
    }

    public enableServiceAccount(organizationId: Organization["id"], serviceAccountId: OrganizationServiceAccount["id"]): Promise<{ enabled: boolean }> {
        return firstValueFrom(
            this.httpClient.put<{ enabled: boolean }>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}/enable`, null)
        );
    }

    public disableServiceAccount(organizationId: Organization["id"], serviceAccountId: OrganizationServiceAccount["id"]): Promise<{ enabled: boolean }> {
        return firstValueFrom(
            this.httpClient.put<{ enabled: boolean }>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}/disable`, null)
        );
    }

    public deleteServiceAccount(organizationId: Organization["id"], serviceAccountId: OrganizationServiceAccount["id"]): Promise<void> {
        return firstValueFrom(this.httpClient.delete<void>(`/api/organizations/${organizationId}/serviceaccounts/${serviceAccountId}`));
    }
}
